import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useInView,
  AnimatePresence,
} from "motion/react";
import Lenis from "lenis";
import "../../styles/ArtSection.css";
import {
  FaCouch,
  FaUtensils,
  FaLock,
  FaLifeRing,
  FaDollarSign,
  FaDraftingCompass,
} from "react-icons/fa";

import img1 from "../../assets/hero/hero1.jpg";
import img2 from "../../assets/hero/hero6.jpg";
import img3 from "../../assets/access1.webp";
import img4 from "../../assets/free.jpg";
import img5 from "../../assets/price1.jpg";
import img6 from "../../assets/hero/hero2.jpg";

const ITEMS = [
  {
    id: 1,
    icon: <FaCouch />,
    title: "Custom Furniture",
    desc: "Handcrafted dining tables, chairs, beds, and storage tailored to your space and preferences.",
    image: img1,
  },
  {
    id: 2,
    icon: <FaUtensils />,
    title: "Kitchen Cabinetry",
    desc: "Premium kitchen solutions designed for functionality, beauty, and seamless storage organization.",
    image: img2,
  },
  {
    id: 3,
    icon: <FaLock />,
    title: "Access Control",
    desc: "Smart security-friendly designs integrated for protected and simple living spaces.",
    image: img3,
  },
  {
    id: 4,
    icon: <FaLifeRing />,
    title: "Free Support",
    desc: "Design guidance, aftercare and installation assistance to make the experience effortless.",
    image: img4,
  },
  {
    id: 5,
    icon: <FaDollarSign />,
    title: "Transparent Pricing",
    desc: "No hidden costs: fair budgets and clear pricing tailored to your needs and vision.",
    image: img5,
  },
  {
    id: 6,
    icon: <FaDraftingCompass />,
    title: "High-Quality Designs",
    desc: "Beautiful, durable, and smartly engineered furniture that elevates modern living.",
    image: img6,
  },
];

const ArtCard = ({ it, idx }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.2, once: true });

  // Scroll animations
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 85%", "end 35%"],
  });

  const drop = useTransform(scrollYProgress, [0, 1], [-28, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [0.94, 1]);
  const imgZoom = useTransform(scrollYProgress, [0, 1], [1.08, 1]);

  // Hover tilt effect
  const [mx, setMx] = useState(0);
  const [my, setMy] = useState(0);

  const rx = useSpring(useMemo(() => my / 12, [my]), {
    stiffness: 220,
    damping: 20,
    mass: 0.4,
  });
  const ry = useSpring(useMemo(() => -mx / 12, [mx]), {
    stiffness: 220,
    damping: 20,
    mass: 0.4,
  });
  const lift = useSpring(0, { stiffness: 220, damping: 20, mass: 0.4 });

  const onPointerMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMx(e.clientX - rect.left - rect.width / 2);
    setMy(e.clientY - rect.top - rect.height / 2);
  };

  return (
    <motion.article
      ref={ref}
      className="art-card stack"
      style={{ y: drop, scale }}
      initial={{ opacity: 0, y: 24, filter: "blur(6px)" }}
      animate={inView ? { opacity: 1, y: 0, filter: "blur(0px)" } : {}}
      transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
        delay: idx * 0.06,
      }}
      onPointerMove={onPointerMove}
      onPointerEnter={() => lift.set(10)}
      onPointerLeave={() => {
        lift.set(0);
        setMx(0);
        setMy(0);
      }}
      whileHover={{ boxShadow: "0 18px 40px rgba(17,17,17,0.12)" }}
    >
      <motion.div className="tilt" style={{ rotateX: rx, rotateY: ry, z: lift }}>
        <motion.div
          className="card-media"
          style={{
            backgroundImage: `url(${it.image})`,
            scale: imgZoom,
          }}
        />
        <div className="card-badge">{it.icon}</div>
        <div className="card-content in-card">
          <h4 className="card-title">{it.title}</h4>
          <p className="card-desc">{it.desc}</p>
        </div>
      </motion.div>
    </motion.article>
  );
};

const ArtSection = () => {
  const wrapRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0); // ✅ for dots

  // Smooth page scrolling
  useEffect(() => {
    const lenis = new Lenis({ smoothWheel: true, lerp: 0.05 });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Auto slide + update active dot
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    let i = 0;
    const step = () => {
      const cards = Array.from(el.querySelectorAll(".art-card"));
      if (!cards.length) return;
      i = (i + 1) % cards.length;
      const x = i * (cards[0].clientWidth + 22);
      el.scrollTo({ left: x, behavior: "smooth" });
      setActiveIndex(i); // ✅ update dots
    };
    const id = setInterval(step, 2000); // faster interval
    return () => clearInterval(id);
  }, []);

  return (
    <section  id="services" className="art-section" style={{ minHeight: "80vh", padding: "100px 20px" }} aria-labelledby="art-we-do-title">
      <div className="art-container">
        <header className="art-header">
          <h2 id="art-we-do-title">The Art we do</h2>
          <p className="art-sub">
            From concept to completion, we offer comprehensive woodworking
            services that bring your vision to life with unmatched quality and
            craftsmanship.
          </p>
        </header>

        {/* Horizontal scroll-snap rail */}
        <div className="art-rail" ref={wrapRef}>
          <AnimatePresence initial={false}>
            {ITEMS.map((it, i) => (
              <ArtCard key={it.id} it={it} idx={i} />
            ))}
          </AnimatePresence>
        </div>

        {/* ✅ Pagination dots */}
        <div className="art-dots">
          {ITEMS.map((_, i) => (
            <span
              key={i}
              className={`dot ${activeIndex === i ? "active" : ""}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ArtSection;
